/* TLC weekly-sheets Shopify section. Product data is rendered by Liquid on each visit. */
(() => {
  'use strict';
  if (customElements.get('tlc-weekly-sheets')) return;

  const SIZE_ORDER = ['Single', 'King Single', 'Double', 'Queen', 'King', 'Super King', 'Split King'];
  let instanceCount = 0;

  // Never pass script/data URLs from merchant-editable content into an image or link.
  const webUrl = (value, sameOrigin = false) => {
    if (typeof value !== 'string' || !value.trim()) return '';
    try {
      const url = new URL(value, window.location.href);
      if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password) return '';
      if (sameOrigin && url.origin !== window.location.origin) return '';
      return url.href;
    } catch (_) { return ''; }
  };
  const text = value => typeof value === 'string' ? value : '';
  const variantId = value => {
    const id = String(value ?? '');
    return /^[1-9]\d*$/.test(id) ? id : '';
  };
  const allowsOne = variant => {
    if (!variant) return false;
    const min = variant.quantityMin == null ? 1 : Number(variant.quantityMin);
    const max = variant.quantityMax == null ? Infinity : Number(variant.quantityMax);
    const increment = variant.quantityIncrement == null ? 1 : Number(variant.quantityIncrement);
    return Number.isInteger(min) && min >= 1 && min <= 1 && max >= 1
      && Number.isInteger(increment) && increment > 0 && 1 % increment === 0;
  };

  class TLCWeeklySheets extends HTMLElement {
    constructor() {
      super();
      this._instance = ++instanceCount;
      this._generation = 0;
      this._cartState = 'idle';
      this._cartMessage = '';
    }

    connectedCallback() {
      this._generation += 1;
      const generation = this._generation;
      this._listeners?.abort();
      this._listeners = new AbortController();
      const initialize = () => {
        if (this.isConnected && generation === this._generation) this.initialize();
      };
      // The asset can be loaded once globally or with a section added in the theme editor.
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initialize, {once: true, signal: this._listeners.signal});
      } else {
        queueMicrotask(initialize);
      }
    }

    disconnectedCallback() {
      this._generation += 1;
      this._listeners?.abort();
      this._intersection?.disconnect();
      if (this._restoreFrame) cancelAnimationFrame(this._restoreFrame);
      if (this._dialog?.open) this._dialog.close();
      // Cancelling a fetch cannot undo a cart mutation. Keep a pending addition locked.
      if (this._cartState === 'pending') {
        this._cartState = 'uncertain';
        this._cartMessage = 'Please check your cart before adding this set again.';
      }
    }

    node(name) { return this.querySelector(`[data-tlc="${name}"]`); }
    listen(target, event, callback) {
      target?.addEventListener(event, callback, {signal: this._listeners.signal});
    }
    setText(name, value) {
      const node = this.node(name);
      if (node) node.textContent = value;
    }
    setImage(node, source, alt) {
      if (!node) return;
      const url = webUrl(source);
      if (url) node.src = url;
      else node.removeAttribute('src');
      node.alt = alt || '';
      node.hidden = !url;
    }

    initialize() {
      this._form = this.node('product-form');
      this._cta = this.node('product-cta');
      this._sizeSelect = this.node('size-select');
      this._colourContainer = this.node('colour-options');
      this._dialog = this.node('photo-dialog');
      if (!this._form || !this._cta || !this._sizeSelect || !this._colourContainer) return;

      // Preserve selection if Shopify removes and reinserts this same element.
      const restoredHandle = this.querySelector('input[data-tlc-colour]:checked')?.value;
      const restoredSize = this._sizeSelect.value;
      try {
        this._config = JSON.parse(this.querySelector('[data-tlc-config]')?.textContent || '{}');
      } catch (_) {
        this.failInitialization();
        return;
      }
      if (!this._config || typeof this._config !== 'object' || Array.isArray(this._config)) {
        this.failInitialization();
        return;
      }
      this._products = Array.isArray(this._config.products)
        ? this._config.products.filter(product => product && typeof product.handle === 'string'
          && typeof product.colour === 'string' && Array.isArray(product.variants))
          .map(product => ({...product, variants: product.variants.filter(variant => variant && typeof variant.size === 'string')})) : [];
      if (!this._products.length) { this.failInitialization(); return; }
      this._cartAddUrl = webUrl(this._config.cartAddUrl, true);
      this._cartUrl = webUrl(this._config.cartUrl, true);
      this._product = this._products.find(product => product.handle === restoredHandle)
        || this._products.find(product => product.colour === this._config.defaultColour)
        || this._products[0];
      this._size = restoredSize || text(this._config.defaultSize) || 'Queen';
      this._galleryHandle = '';
      this._photoIndex = 0;
      try {
        this._money = new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
          style: 'currency', currency: this._config.currency || 'AUD', maximumFractionDigits: 2
        });
      } catch (_) { this._money = null; }

      this.populateColours();
      this.populateSizes();
      this.updateSelection(false);
      this.listen(this._sizeSelect, 'change', () => {
        this._size = this._sizeSelect.value;
        this.clearRecoverableError();
        this.updateSelection();
      });
      this.listen(this._form, 'submit', event => this.submit(event));
      this.listen(window, 'pageshow', () => {
        if (this._restoreFrame) cancelAnimationFrame(this._restoreFrame);
        this._restoreFrame = requestAnimationFrame(() => {
          const handle = this.querySelector('input[data-tlc-colour]:checked')?.value;
          this._product = this._products.find(product => product.handle === handle) || this._product;
          this._size = this._sizeSelect.value || this._size;
          this.populateSizes();
          this.updateSelection(false);
          this.setText('selection-status', '');
        });
      });

      for (const name of ['open-gallery', 'open-mobile-gallery']) {
        this.listen(this.node(name), 'click', () => {
          if (!this.photos().length || !this._dialog) return;
          this.showPhoto(this._photoIndex);
          if (!this._dialog.open) this._dialog.showModal();
        });
      }
      this.listen(this.node('close-gallery'), 'click', () => this._dialog?.close());
      this.listen(this.node('previous-photo'), 'click', () => this.showPhoto(this._photoIndex - 1));
      this.listen(this.node('next-photo'), 'click', () => this.showPhoto(this._photoIndex + 1));
      this.listen(this._dialog, 'click', event => {
        if (event.target === this._dialog) {
          const bounds = this._dialog.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right
            || event.clientY < bounds.top || event.clientY > bounds.bottom) this._dialog.close();
        }
      });
      this.listen(this._dialog, 'keydown', event => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.showPhoto(this._photoIndex + (event.key === 'ArrowLeft' ? -1 : 1));
        }
      });
      // Delegation keeps a single listener per gallery across repeated colour changes.
      for (const name of ['gallery-thumbnails', 'dialog-thumbnails']) {
        const container = this.node(name);
        this.listen(container, 'click', event => {
          const button = event.target.closest('button[data-photo-index]');
          if (button && container.contains(button)) this.showPhoto(Number(button.dataset.photoIndex));
        });
      }
      this.initializeSticky();
      this.dataset.ready = 'true';
    }

    failInitialization() {
      this._cta.disabled = true;
      this._cta.textContent = 'Choose a set on our store';
      const idInput = this.node('variant-id');
      if (idInput) { idInput.value = ''; idInput.disabled = true; }
      this.showError('These options could not be loaded. Use the product link to choose your set.');
      // Keep an incomplete form from posting if a theme script removes disabled styling.
      this.listen(this._form, 'submit', event => event.preventDefault());
    }

    formatPrice(variant) {
      if (!variant) return 'Unavailable in this size';
      if (typeof variant.priceFormatted === 'string' && variant.priceFormatted.trim()) return variant.priceFormatted;
      const cents = Number(variant.priceCents);
      if (!Number.isFinite(cents) || cents < 0) return 'See product for price';
      return this._money ? this._money.format(cents / 100) : `${(cents / 100).toFixed(2)} ${text(this._config.currency)}`;
    }
    selectedVariant() { return this._product.variants.find(variant => variant.size === this._size); }
    canBuy(variant) {
      return Boolean(variant && variant.available === true && variantId(variant.id)
        && !this._product.requiresSellingPlan && !variant.requiresSellingPlan
        && allowsOne(variant) && this._cartAddUrl && this._cartUrl);
    }

    populateColours() {
      this._colourContainer.replaceChildren();
      const groups = [...new Set(this._products.map(product => text(product.group) || 'Colours'))];
      for (const group of groups) {
        const section = document.createElement('div');
        section.className = 'colour-group';
        const name = document.createElement('p');
        name.className = 'colour-group-name';
        name.textContent = group;
        const choices = document.createElement('div');
        choices.className = 'colour-grid';
        for (const product of this._products.filter(item => (text(item.group) || 'Colours') === group)) {
          const label = document.createElement('label');
          label.className = 'swatch-label';
          label.title = product.colour;
          const input = document.createElement('input');
          input.type = 'radio';
          input.name = `tlc-colour-${this._instance}`;
          input.dataset.tlcColour = '';
          input.value = product.handle;
          input.checked = product.handle === this._product.handle;
          const swatch = document.createElement('span');
          swatch.className = 'swatch';
          if (!webUrl(product.swatchImage) && /^(#[0-9a-f]{3,8}|rgba?\([\d\s.,%/]+\))$/i.test(text(product.swatch))) {
            swatch.style.backgroundColor = product.swatch;
          } else {
            const image = document.createElement('img');
            image.loading = 'lazy'; image.width = 44; image.height = 44;
            this.setImage(image, product.swatchImage || product.image, '');
            swatch.append(image);
          }
          const accessibleName = document.createElement('span');
          accessibleName.className = 'swatch-name';
          accessibleName.textContent = product.colour;
          label.append(input, swatch, accessibleName);
          choices.append(label);
        }
        section.append(name, choices);
        this._colourContainer.append(section);
      }
      this.listen(this._colourContainer, 'change', event => {
        const input = event.target;
        if (!input.matches('input[data-tlc-colour]') || !input.checked) return;
        const product = this._products.find(item => item.handle === input.value);
        if (!product) return;
        this._product = product;
        this.clearRecoverableError();
        this.populateSizes();
        this.updateSelection();
      });
    }

    populateSizes() {
      this._sizeSelect.replaceChildren();
      const order = size => SIZE_ORDER.includes(size) ? SIZE_ORDER.indexOf(size) : SIZE_ORDER.length;
      const variants = [...this._product.variants].sort((a, b) => order(a.size) - order(b.size));
      if (!variants.some(variant => variant.size === this._size)) {
        const option = document.createElement('option');
        option.value = this._size;
        option.textContent = `${this._size} — unavailable in this colour`;
        option.selected = true;
        this._sizeSelect.append(option);
      }
      for (const variant of variants) {
        const option = document.createElement('option');
        option.value = variant.size;
        option.textContent = `${variant.size}${variant.available === true ? '' : ' — sold out'}`;
        option.selected = variant.size === this._size;
        this._sizeSelect.append(option);
      }
    }

    updateSelection(announce = true) {
      const product = this._product;
      const variant = this.selectedVariant();
      this.setText('selected-colour', product.colour);
      this.setText('product-price', this.formatPrice(variant));
      if (this._galleryHandle !== product.handle) {
        this._galleryHandle = product.handle;
        this.populateGallery();
      }
      this.setImage(this.node('selection-preview'), product.image || this.photos()[0]?.src, `${product.colour} bedding colour`);
      const pillows = ['Single', 'King Single'].includes(this._size) ? '1 zip pillowcase' : '2 zip pillowcases';
      const fitted = this._size === 'Split King' ? '2 fitted sheets' : '1 fitted sheet';
      this.setText('set-contents', `Includes ${fitted}, 1 top sheet and ${pillows}. Quilt cover sold separately.`);
      const preorder = text(variant?.preorderText) || text(product.preorderText);
      let stockMessage = preorder;
      if (!variant) stockMessage = `${this._size} is not offered in ${product.colour}. Choose another size or colour.`;
      else if (variant.available !== true) stockMessage = `${product.colour} in ${this._size} is currently sold out. Choose another size or colour.`;
      else if (product.requiresSellingPlan || variant.requiresSellingPlan) stockMessage = 'This set requires purchase options. Choose them on the product page.';
      else if (!allowsOne(variant)) stockMessage = 'This set has quantity requirements. Choose your quantity on the product page.';
      const stockNote = this.node('stock-note');
      if (stockNote) { stockNote.textContent = stockMessage; stockNote.hidden = !stockMessage; }

      const idInput = this.node('variant-id');
      if (idInput) {
        idInput.value = variantId(variant?.id);
        idInput.disabled = !this.canBuy(variant) || this._cartState !== 'idle';
      }
      const link = this.node('product-link');
      if (link) {
        const url = webUrl(variant?.url || product.productUrl);
        if (url) link.href = url;
        else link.removeAttribute('href');
        link.hidden = !url;
      }
      const cartLink = this.node('cart-link');
      if (cartLink) {
        if (this._cartUrl) cartLink.href = this._cartUrl;
        else cartLink.removeAttribute('href');
        cartLink.hidden = !this._cartUrl || !['uncertain', 'success'].includes(this._cartState);
      }
      const busy = this._cartState === 'pending';
      this._form.setAttribute('aria-busy', String(busy));
      this._cta.disabled = !this.canBuy(variant) || this._cartState !== 'idle';
      this._cta.textContent = busy ? 'Adding your set…'
        : this._cartState === 'success' ? 'Added to your cart'
        : this._cartState === 'uncertain' ? 'Check your cart'
        : !variant ? 'Choose another size'
        : variant.available !== true ? 'Sold out in this size'
        : !this.canBuy(variant) ? 'Choose options on product page'
        : `Add my ${this._size} set to cart`;
      // Keep the submitted variant and displayed selection aligned while the request is pending.
      this._sizeSelect.disabled = busy;
      for (const input of this._colourContainer.querySelectorAll('input')) input.disabled = busy;
      if (this._cartMessage) this.showError(this._cartMessage);
      if (announce) {
        this.setText('selection-status', `${product.colour}, ${this._size}: ${this.formatPrice(variant)}. ${stockMessage}`.trim());
      }
    }

    photos() {
      const images = Array.isArray(this._product.images) && this._product.images.length
        ? this._product.images : [{src: this._product.image, alt: this._product.imageAlt}];
      return images.filter(photo => photo && webUrl(photo.src));
    }
    populateGallery() {
      const images = this.photos();
      for (const name of ['gallery-thumbnails', 'dialog-thumbnails']) {
        const container = this.node(name);
        if (!container) continue;
        container.replaceChildren();
        images.forEach((photo, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.photoIndex = String(index);
          button.setAttribute('aria-label', `${this._product.colour} photo ${index + 1} of ${images.length}`);
          const image = document.createElement('img');
          image.width = 72; image.height = 72; image.loading = 'lazy';
          this.setImage(image, photo.src, '');
          button.append(image);
          container.append(button);
        });
      }
      for (const name of ['open-gallery', 'open-mobile-gallery']) {
        const button = this.node(name);
        if (button) {
          button.setAttribute('aria-label', `View ${this._product.colour} bedding photos`);
          button.disabled = !images.length;
        }
      }
      this.showPhoto(0);
    }
    showPhoto(index) {
      const images = this.photos();
      if (!images.length) {
        for (const name of ['selected-product-image', 'dialog-image']) this.setImage(this.node(name), '', '');
        this.setText('gallery-caption', 'Photos are available on the product page.');
        return;
      }
      this._photoIndex = ((index % images.length) + images.length) % images.length;
      const photo = images[this._photoIndex];
      const alt = text(photo.alt) || `${this._product.colour} bedding, photo ${this._photoIndex + 1}`;
      for (const name of ['selected-product-image', 'dialog-image']) this.setImage(this.node(name), photo.src, alt);
      const caption = `${this._product.colour} · ${this._photoIndex + 1} of ${images.length} photos`;
      this.setText('gallery-caption', caption);
      this.setText('dialog-caption', caption);
      this.setText('photo-dialog-title', `${this._product.colour} bedding photos`);
      for (const name of ['gallery-thumbnails', 'dialog-thumbnails']) {
        const container = this.node(name);
        if (container) [...container.children].forEach((button, i) => button.setAttribute('aria-pressed', String(i === this._photoIndex)));
      }
      for (const name of ['previous-photo', 'next-photo']) {
        const button = this.node(name);
        if (button) button.disabled = images.length < 2;
      }
    }

    clearRecoverableError() {
      if (this._cartState !== 'idle') return;
      this._cartMessage = '';
      const error = this.node('cart-error');
      if (error) { error.textContent = ''; error.hidden = true; }
    }
    showError(message) {
      const error = this.node('cart-error');
      if (error) { error.textContent = message; error.hidden = false; }
    }
    uncertainCart() {
      this._cartState = 'uncertain';
      this._cartMessage = 'We could not confirm whether this set was added. Please check your cart before trying again. Reload this page only after checking your cart.';
    }

    async submit(event) {
      event.preventDefault();
      // The section owns its form. Do not also invoke a theme's delegated Ajax submitter.
      event.stopPropagation();
      const variant = this.selectedVariant();
      if (this._cartState !== 'idle' || !this.canBuy(variant)) return;
      const generation = this._generation;
      const requestedId = variantId(variant.id);
      this.clearRecoverableError();
      this._cartState = 'pending';
      this.updateSelection(false);
      try {
        const response = await fetch(this._cartAddUrl, {
          method: 'POST', credentials: 'same-origin',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({items: [{id: requestedId, quantity: 1}]})
        });
        let result;
        try { result = await response.json(); } catch (_) { this.uncertainCart(); }
        if (result && response.ok) {
          const items = Array.isArray(result.items) ? result.items : [];
          const confirmed = items.some(item => item && variantId(item.variant_id || item.id) === requestedId
            && Number.isInteger(Number(item.quantity)) && Number(item.quantity) >= 1);
          if (confirmed) {
            this._cartState = 'success';
            this._cartMessage = '';
          } else { this.uncertainCart(); }
        } else if (result && [400, 404, 422].includes(response.status)) {
          // A parsed validation response is actionable. A network/5xx outcome is uncertain.
          this._cartState = 'idle';
          this._cartMessage = text(result.description) || text(result.message)
            || 'This set could not be added. Check your selection and try again.';
        } else { this.uncertainCart(); }
      } catch (_) { this.uncertainCart(); }
      // A section removed by the theme editor must not redirect the merchant later.
      if (!this.isConnected || generation !== this._generation) return;
      this.updateSelection(false);
      if (this._cartState === 'success') {
        this.setText('selection-status', 'Your set was added. Opening your cart.');
        window.location.assign(this._cartUrl);
      } else {
        this.node('cart-error')?.focus({preventScroll: true});
      }
    }

    initializeSticky() {
      this._intersection?.disconnect();
      const sticky = this.querySelector('.mobile-shop');
      const hero = this.querySelector('.hero');
      const shop = this.node('shop') || this.querySelector('.shop');
      const ending = this.querySelector('.closing');
      if (!sticky) return;
      sticky.classList.remove('is-visible');
      sticky.tabIndex = -1;
      sticky.setAttribute('aria-hidden', 'true');
      if (!hero || !shop || !ending || !('IntersectionObserver' in window)) return;
      const visible = new Map([[hero, true], [shop, false], [ending, false]]);
      this._intersection = new IntersectionObserver(entries => {
        for (const entry of entries) visible.set(entry.target, entry.isIntersecting);
        const show = ![...visible.values()].some(Boolean);
        sticky.classList.toggle('is-visible', show);
        sticky.tabIndex = show ? 0 : -1;
        sticky.setAttribute('aria-hidden', String(!show));
      }, {threshold: 0});
      for (const element of visible.keys()) this._intersection.observe(element);
    }
  }

  customElements.define('tlc-weekly-sheets', TLCWeeklySheets);
})();
